import { toast } from "react-toastify";
import { RISK_BADGE } from "pages/CareWatch/utils/constants";
import { all, call, cancel, fork, put, take } from "redux-saga/effects";
import CARE_WATCH_ACTIONS, {
  acknowledgeCareWatchSignals,
  getCareWatchDetail,
  getCareWatchListRequest,
  getCareWatchOverviewRequest,
  setCareWatchRisk,
  getCareWatchList,
  getCareWatchOverview,
  promoteCareWatchSignal,
} from "../actions/careWatch.actions";
import api from "../services/careWatch.services";

function* fetchCareWatchList(action) {
  try {
    yield put(getCareWatchList.pending);
    const payload = action.payload || {};
    const isLive = payload._live === true;
    const filters = { ...payload };
    delete filters._live;

    const response = isLive
      ? yield call(api.getCareWatchLiveList, filters)
      : yield call(api.getCareWatchList, filters);
    yield put(
      getCareWatchList.success(
        isLive
          ? response?.data || []
          : {
              items: response?.data || [],
              total: response?.total ?? (response?.data || []).length,
              page: response?.page,
              serverPaged: true,
            }
      )
    );
  } catch (error) {
    yield put(getCareWatchList.error(error?.message || "Unable to load Care Watch."));
    toast.error("Unable to load Care Watch queue.");
  }
}

function* fetchCareWatchOverview(action) {
  try {
    yield put(getCareWatchOverview.pending);
    const payload = action.payload || {};
    const isLive = payload._live === true;
    const filters = { ...payload };
    delete filters._live;

    const response = isLive
      ? yield call(api.getCareWatchLiveOverview, filters)
      : yield call(api.getCareWatchOverview, filters);
    yield put(getCareWatchOverview.success(response?.data || {}));
  } catch (error) {
    // Silent: the tiles keep their last values and the table still renders.
    yield put(getCareWatchOverview.error(error?.message || "Unable to load Care Watch counts."));
  }
}

function* fetchCareWatchDetail(action) {
  try {
    yield put(getCareWatchDetail.pending);
    const { uuid, incidentKey, view, isLive, shiftId, workerId } = action.payload || {};

    let response;
    if (isLive) {
      response = yield call(api.getCareWatchLiveDetail, shiftId, workerId);
    } else {
      response = yield call(api.getCareWatchSignal, uuid, incidentKey, view);
    }

    yield put(getCareWatchDetail.success(response?.data || null));
  } catch (error) {
    yield put(getCareWatchDetail.error(error?.message || "Unable to load detail."));
  }
}

function* acknowledgeSignals(action) {
  const ids = action.payload?.ids || [];
  if (!ids.length) return;
  try {
    yield put(acknowledgeCareWatchSignals.pending);
    const response = yield call(api.acknowledgeCareWatchSignals, ids);
    yield put(acknowledgeCareWatchSignals.success(response?.data || { ids }));
    toast.success(
      ids.length === 1
        ? "Timesheet acknowledged."
        : `${ids.length} timesheets acknowledged.`
    );
  } catch (error) {
    yield put(acknowledgeCareWatchSignals.error(error?.message));
    toast.error("Couldn't acknowledge the timesheet. Try again.");
  }
}

function* changeRisk(action) {
  const id = action.payload?.id;
  const timesheetId = action.payload?.timesheetId;
  const incidentKey = action.payload?.incidentKey || "";
  const risk = action.payload?.risk;
  if ((!id && !timesheetId) || !risk) return;
  let savedRisk = risk;
  try {
    yield put(setCareWatchRisk.pending(id));
    const response = yield call(api.setCareWatchRisk, {
      id,
      timesheetId,
      incidentKey,
      risk,
    });
    const saved = response?.data || {};
    savedRisk = saved.risk || risk;
    yield put(
      setCareWatchRisk.success({
        id: saved.id || id,
        timesheetId: saved.timesheet_id || timesheetId,
        incidentKey: saved.incident_key ?? incidentKey,
        risk: savedRisk,
      })
    );
  } catch (error) {
    yield put(setCareWatchRisk.error(error?.message));
    toast.error("Couldn't update the risk. Try again.");
    return;
  }

  toast.success(`Risk set to ${RISK_BADGE[savedRisk]?.label || "updated"}.`);
  // Counts cover every matching row, not the page on screen. Refresh them
  // and the current page without replacing the whole screen.
  if (action.payload?.overviewFilters) {
    yield put(getCareWatchOverviewRequest(action.payload.overviewFilters));
  }
  if (action.payload?.listFilters && !action.payload.listFilters._live) {
    yield put(getCareWatchListRequest(action.payload.listFilters));
  }
}

function* promoteSignal(action) {
  const id = action.payload?.id;
  if (!id) return;
  try {
    yield put(promoteCareWatchSignal.pending);
    const response = yield call(api.promoteCareWatchSignal, id);
    yield put(promoteCareWatchSignal.success(response?.data || { id }));
    toast.success("Opening a draft incident form.");
  } catch (error) {
    yield put(promoteCareWatchSignal.error(error?.message));
    toast.error("Couldn't create the incident. Try again.");
  }
}

function* watchCall(actionType, worker) {
  let task;
  while (true) {
    const action = yield take([actionType, CARE_WATCH_ACTIONS.RESET]);
    if (task) {
      yield cancel(task);
      task = null;
    }
    if (action.type !== CARE_WATCH_ACTIONS.RESET) {
      task = yield fork(worker, action);
    }
  }
}

export default function* careWatchSagas() {
  yield all([
    watchCall(CARE_WATCH_ACTIONS.LIST_CALL, fetchCareWatchList),
    watchCall(CARE_WATCH_ACTIONS.OVERVIEW_CALL, fetchCareWatchOverview),
    watchCall(CARE_WATCH_ACTIONS.DETAIL_CALL, fetchCareWatchDetail),
    watchCall(CARE_WATCH_ACTIONS.ACKNOWLEDGE_CALL, acknowledgeSignals),
    watchCall(CARE_WATCH_ACTIONS.SET_RISK_CALL, changeRisk),
    watchCall(CARE_WATCH_ACTIONS.PROMOTE_CALL, promoteSignal),
  ]);
}
